// frontend/src/components/widgets/RSSWidgetConfig.js - WITH BLUE THEME + VISUAL PREVIEW
import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle, Loader, Rss, Layout, RefreshCw, Image, FileText, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RSSWidgetConfig = ({ existingConfig, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  // ✅ PRESET DEFINITIONS
  const RSS_PRESETS = {
    news: {
      layout: 'ticker',
      maxItems: 20,
      updateInterval: 300,
      showImages: true,
      showDescription: false,
      showDate: true,
      itemDuration: 10,
      theme: 'dark'
    },
    blog: {
      layout: 'card',
      maxItems: 9,
      updateInterval: 600,
      showImages: true,
      showDescription: true,
      showDate: true,
      itemDuration: 10,
      theme: 'light'
    },
    showcase: {
      layout: 'fullscreen',
      maxItems: 10,
      updateInterval: 600,
      showImages: true,
      showDescription: true,
      showDate: true,
      itemDuration: 15,
      theme: 'blue'
    }
  };

  const [config, setConfig] = useState({
    name: existingConfig?.name || '',
    feedUrl: existingConfig?.config?.feedUrl || '',
    layout: existingConfig?.config?.layout || 'card',
    maxItems: existingConfig?.config?.maxItems || 10,
    updateInterval: existingConfig?.config?.updateInterval || 600,
    showImages: existingConfig?.config?.showImages !== false,
    showDescription: existingConfig?.config?.showDescription !== false,
    showDate: existingConfig?.config?.showDate !== false,
    itemDuration: existingConfig?.config?.itemDuration || 10,
    theme: existingConfig?.config?.theme || 'light'
  });

  const [selectedPreset, setSelectedPreset] = useState('custom');

  const [validation, setValidation] = useState({
    isValidating: false,
    isValid: false,
    feedTitle: '',
    itemCount: 0,
    error: ''
  });

  const [previewData, setPreviewData] = useState(null);

  // ✅ PRESET HANDLER
  const handlePresetChange = (presetKey) => {
    if (presetKey === 'custom') {
      setSelectedPreset('custom');
      return;
    }

    const preset = RSS_PRESETS[presetKey];
    if (preset) {
      setConfig(prev => ({
        ...prev,
        ...preset
      }));
      setSelectedPreset(presetKey);
    }
  };

  // Validate RSS Feed URL
  const validateFeed = async (url) => {
    if (!url || url.trim() === '') {
      setValidation({ isValidating: false, isValid: false, feedTitle: '', itemCount: 0, error: '' });
      return;
    }

    setValidation({ isValidating: true, isValid: false, feedTitle: '', itemCount: 0, error: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/rss/validate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ url })
        }
      );

      const data = await response.json();

      if (data.valid) {
        setValidation({
          isValidating: false,
          isValid: true,
          feedTitle: data.feedTitle,
          itemCount: data.itemCount,
          error: ''
        });
        
        loadPreview(url);
      } else {
        setValidation({
          isValidating: false,
          isValid: false,
          feedTitle: '',
          itemCount: 0,
          error: data.error || t('rss.errors.invalidFeed')
        });
      }
    } catch (error) {
      console.error('Feed validation error:', error);
      setValidation({
        isValidating: false,
        isValid: false,
        feedTitle: '',
        itemCount: 0,
        error: t('rss.errors.validationFailed')
      });
    }
  };

  // Load feed preview
  const loadPreview = async (url) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/rss/feed?url=${encodeURIComponent(url)}&maxItems=3`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        setPreviewData(data.feed);
      }
    } catch (error) {
      console.error('Preview load error:', error);
    }
  };

  // Debounced feed validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (config.feedUrl) {
        validateFeed(config.feedUrl);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [config.feedUrl]);

  const handleSave = () => {
    if (!config.name || !config.feedUrl) {
      alert(t('rss.errors.requiredFields'));
      return;
    }

    if (!validation.isValid) {
      alert(t('rss.errors.invalidFeed'));
      return;
    }

    onSave({
      name: config.name,
      config: {
        feedUrl: config.feedUrl,
        layout: config.layout,
        maxItems: config.maxItems,
        updateInterval: config.updateInterval,
        showImages: config.showImages,
        showDescription: config.showDescription,
        showDate: config.showDate,
        itemDuration: config.itemDuration,
        theme: config.theme
      }
    });
  };

  const getLayoutIcon = (layout) => {
    switch (layout) {
      case 'card': return '📰';
      case 'ticker': return '📊';
      case 'fullscreen': return '🖼️';
      default: return '📄';
    }
  };

  // ✅ GET THEME STYLES FOR PREVIEW
  const getThemeStyles = () => {
    const themes = {
      light: {
        bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
        card: 'bg-white border-gray-200',
        text: 'text-gray-900',
        textSecondary: 'text-gray-600',
        accent: 'bg-blue-600'
      },
      dark: {
        bg: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
        card: 'bg-gray-800/50 border-gray-700',
        text: 'text-gray-100',
        textSecondary: 'text-gray-300',
        accent: 'bg-cyan-500'
      },
      blue: {
        bg: 'bg-gradient-to-br from-[#3b82f6] via-[#2563eb] to-[#1d4ed8]',
        card: 'bg-white/10 backdrop-blur-md border-white/20',
        text: 'text-white',
        textSecondary: 'text-white/90',
        accent: 'bg-white'
      },
      colored: {
        bg: 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700',
        card: 'bg-white/10 backdrop-blur-md border-white/20',
        text: 'text-white',
        textSecondary: 'text-white/90',
        accent: 'bg-white'
      },
      classic: {
        bg: 'bg-blue-600',
        card: 'bg-blue-700/30 border-white/20',
        text: 'text-white',
        textSecondary: 'text-white/90',
        accent: 'bg-white'
      },
      classicBlue: {
        bg: 'bg-gradient-to-br from-[#1e3c72] via-[#2a5298] to-[#1e3c72]',
        card: 'bg-white/10 backdrop-blur-md border-white/20',
        text: 'text-white',
        textSecondary: 'text-white/90',
        accent: 'bg-white'
      }
    };
    return themes[config.theme] || themes.light;
  };

  const themeStyles = getThemeStyles();

  // ✅ PRESET OPTIONS
  const presetOptions = [
    { value: 'custom', label: t('rss.presetCustom') },
    { value: 'news', label: t('rss.presetNews'), desc: t('rss.presetNewsDesc') },
    { value: 'blog', label: t('rss.presetBlog'), desc: t('rss.presetBlogDesc') },
    { value: 'showcase', label: t('rss.presetShowcase'), desc: t('rss.presetShowcaseDesc') }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 rounded-lg">
            <Rss className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('rss.configTitle')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('rss.configSubtitle')}
            </p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
        
        {/* Widget Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.widgetName')} *
          </label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value })}
            placeholder={t('rss.widgetNamePlaceholder')}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
          />
        </div>

        {/* Feed URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('rss.feedUrl')} *
          </label>
          <div className="relative">
            <input
              type="url"
              value={config.feedUrl}
              onChange={(e) => {
                setConfig({ ...config, feedUrl: e.target.value });
                setSelectedPreset('custom');
              }}
              placeholder={t('rss.feedUrlPlaceholder')}
              className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
            />
            {validation.isValidating && (
              <Loader className="absolute right-3 top-3 animate-spin text-gray-400" size={20} />
            )}
            {!validation.isValidating && validation.isValid && (
              <CheckCircle className="absolute right-3 top-3 text-green-500" size={20} />
            )}
            {!validation.isValidating && validation.error && (
              <AlertCircle className="absolute right-3 top-3 text-red-500" size={20} />
            )}
          </div>
          {validation.isValid && (
            <p className="mt-2 text-sm text-green-600 dark:text-green-400">
              ✅ {validation.feedTitle} ({validation.itemCount} {t('rss.items')})
            </p>
          )}
          {validation.error && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              ❌ {validation.error}
            </p>
          )}
        </div>

        {/* ✅ PRESET SELECTOR */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Sparkles size={20} />
            {t('rss.presets')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {presetOptions.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetChange(preset.value)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  selectedPreset === preset.value
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-700'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {preset.label}
                </div>
                {preset.desc && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {preset.desc}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Layout Selection */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Layout size={20} />
            {t('rss.layoutSettings')}
          </h3>

          <div className="grid grid-cols-3 gap-4">
            {['card', 'ticker', 'fullscreen'].map((layout) => (
              <button
                key={layout}
                onClick={() => {
                  setConfig({ ...config, layout });
                  setSelectedPreset('custom');
                }}
                className={`p-4 border-2 rounded-lg transition-all ${
                  config.layout === layout
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-orange-300'
                }`}
              >
                <div className="text-4xl mb-2">{getLayoutIcon(layout)}</div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t(`rss.layout${layout.charAt(0).toUpperCase() + layout.slice(1)}`)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Display Options */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Image size={20} />
            {t('rss.displayOptions')}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="checkbox"
                checked={config.showImages}
                onChange={(e) => {
                  setConfig({ ...config, showImages: e.target.checked });
                  setSelectedPreset('custom');
                }}
                className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('rss.showImages')}</span>
            </label>

            <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="checkbox"
                checked={config.showDescription}
                onChange={(e) => {
                  setConfig({ ...config, showDescription: e.target.checked });
                  setSelectedPreset('custom');
                }}
                className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('rss.showDescription')}</span>
            </label>

            <label className="flex items-center gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="checkbox"
                checked={config.showDate}
                onChange={(e) => {
                  setConfig({ ...config, showDate: e.target.checked });
                  setSelectedPreset('custom');
                }}
                className="w-5 h-5 text-orange-500 rounded focus:ring-orange-500"
              />
              <span className="text-gray-700 dark:text-gray-300">{t('rss.showDate')}</span>
            </label>
          </div>
        </div>

        {/* Advanced Settings */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <RefreshCw size={20} />
            {t('rss.advancedSettings')}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('rss.maxItems')}
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={config.maxItems}
                onChange={(e) => {
                  setConfig({ ...config, maxItems: parseInt(e.target.value) });
                  setSelectedPreset('custom');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('rss.updateInterval')}
              </label>
              <input
                type="number"
                min="300"
                value={config.updateInterval}
                onChange={(e) => {
                  setConfig({ ...config, updateInterval: parseInt(e.target.value) });
                  setSelectedPreset('custom');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('rss.minimumSeconds')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('rss.itemDuration')}
              </label>
              <input
                type="number"
                min="3"
                max="60"
                value={config.itemDuration}
                onChange={(e) => {
                  setConfig({ ...config, itemDuration: parseInt(e.target.value) });
                  setSelectedPreset('custom');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('rss.theme')}
              </label>
              <select
                value={config.theme}
                onChange={(e) => {
                  setConfig({ ...config, theme: e.target.value });
                  setSelectedPreset('custom');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="light">{t('rss.themeLight')}</option>
                <option value="dark">{t('rss.themeDark')}</option>
                <option value="blue">{t('rss.themeBlue')}</option>
                <option value="classicBlue">{t('rss.themeClassicBlue')}</option>
                <option value="colored">{t('rss.themeColored')}</option>
                <option value="classic">{t('rss.themeClassic')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* ✅ VISUAL PREVIEW */}
        {previewData && previewData.items.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Sparkles size={20} />
              {t('rss.preview')}
            </h3>
            
            <div className={`${themeStyles.bg} rounded-xl p-6 space-y-4`}>
              {/* Preview Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`${themeStyles.accent} p-2 rounded-lg`}>
                  <Rss className={config.theme === 'light' ? 'text-white' : 'text-gray-900'} size={20} />
                </div>
                <div>
                  <h4 className={`font-bold text-lg ${themeStyles.text}`}>
                    {previewData.feedTitle}
                  </h4>
                  <p className={`text-sm ${themeStyles.textSecondary}`}>
                    {previewData.items.length} {t('rss.items')}
                  </p>
                </div>
              </div>

              {/* Preview Items */}
              <div className="space-y-3">
                {previewData.items.slice(0, 2).map((item, index) => (
                  <div 
                    key={index} 
                    className={`${themeStyles.card} rounded-lg p-4 border-2 transition-all hover:scale-[1.02]`}
                  >
                    <div className="flex items-start gap-3">
                      {config.showImages && item.imageUrl && (
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900/20">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h5 className={`font-semibold ${themeStyles.text} mb-1 line-clamp-2`}>
                          {item.title}
                        </h5>
                        {config.showDescription && item.description && (
                          <p className={`text-sm ${themeStyles.textSecondary} line-clamp-2 mb-2`}>
                            {item.description.replace(/<[^>]*>/g, '')}
                          </p>
                        )}
                        {config.showDate && (
                          <div className={`flex items-center gap-2 text-xs ${themeStyles.textSecondary}`}>
                            <span>📅</span>
                            <span>{new Date(item.pubDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview Info */}
              <div className={`text-center text-sm ${themeStyles.textSecondary} pt-2`}>
                <span className="opacity-75">
                  Layout: <strong>{config.layout}</strong> • Theme: <strong>{config.theme}</strong>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleSave}
          disabled={!validation.isValid || !config.name}
          className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={20} />
          {t('common.save')}
        </button>
        <button
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};

export default RSSWidgetConfig;